import API from '../../../api/endpoints.js'
import event from '../../../event.js'
import TransformMixin from './TransformMixin'

/*
* The cloud-suite datasource has a few subtlties that
* need to be catered for when both loading from DB
* and saving to DB (e.g. date formats, inability
* to overwrite Big decimal to null, having to
* send -1 as intent to nullify foreign key
* field). Therefore, when saving a new or
* initial entity, we can and should null
* -ify empty fields, but we need to ap
* -ply transformations on load and
* update
*/
export default {
  mixins: [ TransformMixin ],
  data () {
    return {
      initialEntityValue: '',
      leaveEntityValue: ''
    }
  },
  computed: {
    baseEntity () {
      return this.$route.params.id ? this.$store.getters[`admin/${this.entityName}`] : this.getBaseData()
    },
    isValid () {
      window.console.warn(`If you can view this message, Admin Entity component '${this.$options.name}' has not overwritten the 'isValid' computed property`)
    },
    saveOrUpdate () {
      return this.entity.id ? 'Update' : 'Save'
    },
    isNew () {
      return !this.entity.id
    },
    isUpdate () {
      return this.entity.id !== null && this.entity.id > 0
    },
    transformOnLoad () {},
    currencies () {
      return this.$store.getters['systemOption/currencies']
    },
    countries () {
      return this.$store.getters['systemOption/countries']
    },
    updateCacheOnSave () {
      return this.hasOwnProperty('shouldUpdateCache') && this.shouldUpdateCache && this.hasOwnProperty('cachePropertyName')
    }
  },
  methods: {
    wrap (obj) {
      let mut = { ...obj }
      this.boxes.forEach(({prop, id}) => {
        if (mut[prop]) {
          mut[prop] = { [id]: mut[prop] }
        }
      })
      return mut
    },
    unbox () {
      this.boxes.forEach(({prop, id}) => {
        if (this.entity[prop] && this.entity[prop].hasOwnProperty(id)) {
          this.entity[prop] = this.entity[prop][id]
        }
      })
      this.runCustomUnwrap()
    },
    /*
      Late model changes meant some nested objects nested
      a little extra logic not captured in 'unbox'
    */
    runCustomUnwrap () {
      return
    },
    isEqual (a, b, prop) {
      return a && a[prop] === b[prop]
    },
    runLoadTransformations () {
      if (this.onLoadTransformations) {
        for (let prop in this.onLoadTransformations) {
          if (this.entity.hasOwnProperty(prop)) {
            this.entity[prop] = this.onLoadTransformations[prop](this.entity[prop])
          }
        }
      }
    },
    initData () {
      for (let prop in this.baseEntity) {
        this.entity[prop] = this.baseEntity[prop]
      }
      this.unbox()
      this.runLoadTransformations()
      if (this.refresh && typeof this.refresh == 'function') {
        this.$nextTick(this.refresh)
      }
    },
    clearData () {
      const data = this.getBaseData()
      for (let prop in data) {
        this.entity[prop] = data[prop]
      }
      this.unbox()
    },
    respondToFilterRequest () {
      window.console.warn(`If you can view this message, Admin Entity component '${this.$options.name}' has not overwritten the 'respondToFilterRequest' method properly`)
    },
    setEntityStringValue (prop) {
      this[prop] = JSON.stringify(this.entity)
    },
    checkForUnsavedChanges () {
      return this.initialEntityValue !== this.leaveEntityValue
    },
    preSaveRoutine () {
      /*
        Late model changes in some areas forced some pre save procedures
        If the 'extending' component overwrites this, it must
        return a promise
      */
      return new Promise((resolve, reject) => resolve())
    },
    postSaveRoutine (data) {
      return new Promise((resolve, reject) => resolve())
    },
    save (shouldExit) {
      if (this.isValid) {
        this.preSaveRoutine().then(res => {
          if (this.entity.id) {
            this.doUpdate(shouldExit)
          } else {
            this.doSave(shouldExit)
          }
        }).catch(err => {
          console.log(err)
          event.$emit('toast', { type: 'error', message: 'An error occured; please try again or contact support' })
        })
      } else {
        event.$emit('toast', { type: 'warning', message: 'Complete all required fields before saving' })
      }
    },
    doSave (shouldExit) {
      this.$http.post(API.CREATE_ADMIN_ENTITY(this.entityName), this.nullifyEntity)
      .then(({data}) => {
        event.$emit('toast', { type: 'success', message: 'Entity saved' })
        if (this.updateCacheOnSave) {
          this.$store.dispatch('systemOption/updateCache', { cacheProperty: this.cachePropertyName, data })
        }
        if (this.entity) {
          this.entity.id = data.id
        }
        this.postSaveRoutine(data).then(() => {
          if (!shouldExit) {
            this.goToEditScreen(data.id)
          } else {
            this.initData()
          }
        })
      })
      .catch(err => {
        console.log(err)
        event.$emit('toast', { type: 'error', message: 'Entity not saved' })
      })
    },
    doUpdate (shouldExit) {
      return this.$http.put(API.UPDATE_ADMIN_ENTITY(this.entityName, this.entity.id), this.transformedEntity)
      .then(({data}) => {
        event.$emit('toast', { type: 'success', message: 'Entity saved' })
        if (this.updateCacheOnSave) {
          this.$store.dispatch('systemOption/updateCache', { cacheProperty: this.cachePropertyName, data })
        }
        this.postSaveRoutine(data).then(() => {
          if (shouldExit) {
            this.$router.push({ name: this.newEntityScreen })
          }
        })
      })
      .catch(err => {
        console.log(err)
        event.$emit('toast', { type: 'error', message: 'Entity not saved' })
      })
    },
    goToEditScreen (id) {
      let hash = new Date().getTime() / 1000
      this.$router.push({ name: this.editEntityScreen, params: {id: btoa(`${hash}#mai#${id}#mai#${hash}`)} })
    },
    findDataById (id) {
      return new Promise((resolve, reject) => {
        this.$http.get(API.GET_ADMIN_ENTITY_BY_ID(this.entityName, id))
        .then(({data}) => {
          resolve(data)
        })
        .catch(err => {
          console.log(err)
          reject(err)
        })
      })
    },
    saveRelatedEntity (entity, json) {
      return this.$store.dispatch('admin/saveEntity', { entity, json })
    },
    deleteRelatedEntity (entity, id) {
      return this.$store.dispatch('admin/deleteEntity', { entity, id })
    }
  },
  watch: {
    '$route' (val, old) {
      this.initData()
    }
  },
  mounted () {
    this.initData()
    this.setEntityStringValue('initialEntityValue')
    this.$parent.$on('clear-screen', this.clearData)
  },
  beforeDestroy () {
    this.$parent.$off('clear-screen', this.clearData)
  }
}



// WEBPACK FOOTER //
// ./src/modules/admin/mixins/AdminEntityMixin.js